import { Component, ChangeDetectionStrategy, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, PRIMARY_OUTLET, ParamMap, Router, RouterLink, UrlTree } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DatePipe, NgClass } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subscription, catchError, concatMap, filter, finalize, first, from, map, of, take, toArray } from 'rxjs';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';

import { DeliveryTrackingService, TRACKING_KINDS, TrackingKind, TrackingLine, TrackingPiece, TrackingQuery } from './delivery-tracking.service';
import { DeliveryLineQty, TourReturnService } from '@app/sales/tour-return/tour-return.service';
import { ProductsService } from '@app/sales/products/products.service';
import { CommonService } from '@app/common/common.service';
import { PartnerService } from '@app/common/partner/partner.service';
import { ValuesService } from '@app/common/values/values.service';
import { AlertService } from '@app/shared/alert.service';
import { SpinnerService } from '@app/shared/spinner.service';
import { C_value, Partner, Store, User } from '@app/shared/_models/common';
import { roundHalfAway } from '@app/shared/_models/sales';
import { ScanResult, looksLikeFreeText, scanLabel, scanRejection, splitQtyPrefix } from '@app/shared/_models/scan';
import { C_VALUES_DELIVERY_SLOT, getOrderLabel, salesOrderColors, sales_order_mode } from '@app/shared/constants';
import { SalesOrderState } from '@app/shared/constants-server';
import { pickedDateStr, todayStr, yearsBefore } from '@app/shared/time';
import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { SearchPartnerComponent } from '@app/shared/search-partner/search-partner.component';
import { SearchProductComponent } from '@app/shared/search-product/search-product.component';
import { SelectDateComponent } from '@app/shared/select-date/select-date.component';
import { askDate } from '@app/shared/confirm-date-dialog/confirm-date-dialog.component';
import { SmartInputComponent } from '@app/shared/smart-input/smart-input.component';
import { RentalDeliveryDialogComponent } from '@app/rental/rental-delivery-dialog/rental-delivery-dialog.component';
import { rentalSubtitle, rentalTitle } from '@app/sales/rental-billing/rental-billing.labels';

// Mêmes règles de lecture d'URL que F4 et la facturation V2 : jour réel AAAA-MM-JJ, id entier positif sur 32 bits.
function urlDay(v: string | null): Date | null {
  return v && /^\d{4}-\d{2}-\d{2}$/.test(v) && dayjs(v).isValid() && dayjs(v).format('YYYY-MM-DD') === v ? dayjs(v).toDate() : null;
}

function urlId(v: string | null): string | null {
  return v !== null && /^\d{1,10}$/.test(v) && +v > 0 && +v <= 2147483647 ? String(+v) : null;
}

function pathOf(t: UrlTree): string {
  return t.root.children[PRIMARY_OUTLET]?.segments.map((s) => s.path).join('/') ?? '';
}

function esc(v: string): string {
  return v.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

// Quantité saisie : positive, au centième (même contrôle que le serveur, event 705).
function isQty(v: number | null | undefined): v is number {
  return v !== null && v !== undefined && Number.isFinite(v) && v > 0 && roundHalfAway(v, 2) === v;
}

function frDay(day: string | null | undefined): string {
  return day ? dayjs(day).format('DD/MM/YY') : '';
}

function fmtQty(v: number): string {
  return (+v).toLocaleString('fr-FR', { maximumFractionDigits: 2 });
}

type Period = 'day' | 'month' | 'days30' | 'months12';

const DEFAULT_KINDS: TrackingKind[] = ['planned', 'remaining'];

export const KIND_LABELS: Record<TrackingKind, string> = { delivered: 'Livrés', planned: 'Programmés', remaining: 'Reste à livrer' };

interface TrackingFilter {
  kinds: TrackingKind[];
  from?: Date;
  to?: Date;
  customer?: string;
  product?: string;
  canceled: boolean;
  rental: boolean;
  store: string;
  user: string;
}

// Nature d'une ligne autre qu'un article vendu, dite en clair (order_line_type).
const LINE_TYPES: Record<string, string> = { ITEM_RETURN: 'retour', COMPONENT: 'composant', RENTAL: 'location', RENTAL_RETURN: 'retour de location', DEPOSIT_DEDUCTION: "déduction d'acompte" };

export interface LineView {
  l: TrackingLine;
  name: string;                   // intitulé du contrat de location pour une ligne louée, comme le BL imprimé ; l'article sinon
  article: string | null;         // l'article sous l'intitulé du contrat
  typLabel: string;               // vide pour un article vendu (ITEM)
  units: number | null;           // qty × unités du conditionnement, quand il en contient plusieurs
  hit: boolean;                   // ligne du produit cherché ; sans produit cherché, toutes le sont
}

export interface PieceView {
  key: string;                    // b<BL> ou r<commande> : unique dans la liste
  p: TrackingPiece;
  kind: TrackingKind;
  title: string;                  // « BL N°… » ou « Reste »
  when: string;                   // date propre du BL, en note à côté de la date de validation
  modeLabel: string;
  stateLabel: string;
  stateColor: string;
  canceledOrder: boolean;
  rental: boolean;                // BL d'un dossier de location
  passage: number | null;         // BL du dossier de location livré avec ce BL (lui-même pour un dossier) : cochés et livrés ensemble
  lines: LineView[];
}

export interface GroupView { key: string; name: string; city: string; pieces: PieceView[]; }

// Ligne cochée pour « Annuler le reste » : la quantité proposée est tout le reste, réductible.
interface CancelEntry { orderId: number; customer: string; name: string; qty: number | null; max: number; }

// Poses (attendues : programmé + reste ; livrées) et reprises (BL −1 programmés ; faites) du produit, chacune avec ses clients.
export interface ProductSummary { units: number; planned: number; remaining: number; delivered: number; customers: number; back: number; backDone: number; backCustomers: number; }

export function productSummaryOf(pieces: { kind: TrackingKind; p: { customer_id: number }; lines: { l: { product: number; qty: number | string; qtybaseuom?: number | string | null } }[] }[], product: number): ProductSummary {
  const s = { units: 0, planned: 0, remaining: 0, delivered: 0, customers: 0, back: 0, backDone: 0, backCustomers: 0 };
  const customers = new Set<number>();
  const backCustomers = new Set<number>();
  for (const v of pieces) {
    for (const lv of v.lines) {
      if (lv.l.product !== product) continue;
      const u = +lv.l.qty * (lv.l.qtybaseuom && +lv.l.qtybaseuom > 0 ? +lv.l.qtybaseuom : 1);
      if (u < 0) {
        if (v.kind === 'delivered') s.backDone -= u;
        else { s.back -= u; backCustomers.add(v.p.customer_id); }
        continue;
      }
      s[v.kind] += u;
      if (v.kind !== 'delivered') customers.add(v.p.customer_id);
    }
  }
  const r = (x: number) => roundHalfAway(x, 2);
  return { units: r(s.planned + s.remaining), planned: r(s.planned), remaining: r(s.remaining), delivered: r(s.delivered), customers: customers.size, back: r(s.back), backDone: r(s.backDone), backCustomers: backCustomers.size };
}

@Component({
  selector: 'app-delivery-tracking',
  templateUrl: './delivery-tracking.component.html',
  styleUrls: ['./delivery-tracking.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MenuCommonComponent, SearchPartnerComponent, SearchProductComponent, SelectDateComponent, SmartInputComponent, RouterLink, FormsModule, NgClass, DatePipe, MatButtonModule, MatIconModule, MatTooltipModule, MatCheckboxModule, MatProgressSpinnerModule],
})
export class DeliveryTrackingComponent implements OnInit {
  private trackingService = inject(DeliveryTrackingService);
  private tourReturnService = inject(TourReturnService);
  private productsService = inject(ProductsService);
  private commonService = inject(CommonService);
  private partnerService = inject(PartnerService);
  private valuesService = inject(ValuesService);
  private alertService = inject(AlertService);
  private spinner = inject(SpinnerService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);

  public readonly kinds = TRACKING_KINDS;
  public readonly kindLabels = KIND_LABELS;

  public loading = false;
  public loadFailed = false;
  public loadedAt: Date | null = null;
  private raw: TrackingPiece[] = [];        // la dernière lecture, telle que le serveur l'a rendue
  public pieces: PieceView[] = [];          // ce que le serveur a renvoyé pour les critères serveur
  public visible: PieceView[] = [];         // ce que magasin et employé laissent voir
  public groups: GroupView[] = [];          // les mêmes, rangés par client
  public folded = false;                    // en-têtes seuls
  public selectedBl = new Set<number>();    // BL programmés à passer à livré
  public cancel = new Map<number, CancelEntry>();   // lignes de reste à annuler, par id de ligne de commande

  public stores: Store[] = [{ id: -1, name: 'Tous' }];
  public users: User[] = [{ id: -1, store: -1, currentstore: 0, firstname: 'Tous' }];
  private slots: C_value[] = [];
  private storesLoaded = false;
  private usersLoaded = false;

  private defaultFilter(): TrackingFilter {
    return { kinds: [...DEFAULT_KINDS], canceled: false, rental: false, store: '-1', user: '-1' };
  }
  public filter: TrackingFilter = this.defaultFilter();
  public customer: Partner | null = null;
  public productName = '';

  @ViewChild('orderIdNav') orderIdNavInput?: SmartInputComponent;
  public orderIdText = '';
  public orderIdHref: string | null = null;
  public orderId: string | null = null;     // commande dont l'écran montre seule les pièces, tous états confondus

  private alive = true;
  private started = false;
  private ownWrites = 0;
  private syncPending = false;
  private loadSub?: Subscription;

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => this.alive = false);
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((q) => this.applyUrl(q));
    this.commonService.getStores().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: (stores: Store[]) => { this.stores = [...this.stores, ...stores]; this.storesLoaded = true; this.reconcile(); } });
    this.commonService.getUsers().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: (users: User[]) => { this.users = [...this.users, ...users]; this.usersLoaded = true; this.reconcile(); } });
    this.valuesService.getValues$(C_VALUES_DELIVERY_SLOT).pipe(take(1), catchError(() => of([] as C_value[])), takeUntilDestroyed(this.destroyRef)).subscribe((slots: C_value[]) => { this.slots = slots ?? []; this.rebuild(); });
  }

  ngAfterViewInit(): void {
    setTimeout(() => { if (!this.alive) return; this.started = true; this.syncUrl(); });
  }

  // --- Chargement : client, produit, dates, types et commande vont au serveur ; magasin et employé se trient ici ---

  private query(): TrackingQuery {
    if (this.orderId) return { kinds: [...TRACKING_KINDS], canceled: true, order: +this.orderId, rental: true };
    const f = this.filter;
    return { kinds: f.kinds, from: f.from, to: f.to, customer: f.customer ? +f.customer : undefined, product: f.product ? +f.product : undefined, canceled: f.canceled, rental: f.rental };
  }

  load(): void {
    this.loadSub?.unsubscribe();
    if (!this.orderId && !this.filter.kinds.length) {
      this.loading = false;
      this.raw = [];
      this.rebuild();
      return;
    }
    this.loading = true;
    this.loadFailed = false;
    this.loadSub = this.trackingService.getPieces$(this.query())
      .pipe(first(), finalize(() => { this.loading = false; }))
      .subscribe({
        next: (data: TrackingPiece[]) => {
          this.raw = data ?? [];
          this.loadedAt = new Date();
          this.rebuild();
        },
        error: (e) => {
          this.loadFailed = true;
          this.raw = [];
          this.rebuild();
          const serverMsg = e.error?.message || e.error || e.message || 'Erreur inconnue';
          Swal.fire({ title: 'Erreur', text: `Erreur au chargement du suivi des livraisons.\nDétails: ${serverMsg}`, icon: 'error', confirmButtonText: 'Fermer' });
          console.error(e);
        },
      });
  }

  // Tout ce que le gabarit lit est calculé ici, une fois par lecture ou par changement de filtre local.
  private rebuild(): void {
    const product = this.filter.product && !this.orderId ? +this.filter.product : null;
    this.pieces = this.raw.map((p) => this.pieceView(p, product));
    if (product !== null && this.productName === 'Produit n° ' + product) {
      const name = this.raw.flatMap((p) => p.lines ?? []).find((l) => l.product === product)?.product_name;
      if (name) this.productName = name;
    }
    this.applyLocal();
  }

  private pieceView(p: TrackingPiece, product: number | null): PieceView {
    const lines: LineView[] = (p.lines ?? []).map((l) => ({
      l,
      name: rentalTitle(l) || 'Produit ' + l.product,
      article: rentalSubtitle(l),
      typLabel: l.typ === 'ITEM' ? '' : LINE_TYPES[l.typ] ?? l.typ,
      units: l.qtybaseuom && +l.qtybaseuom > 1 ? roundHalfAway(+l.qty * +l.qtybaseuom, 2) : null,
      hit: product === null || l.product === product,
    }));
    return {
      key: (p.kind === 'remaining' ? 'r' : 'b') + (p.delivery_id ?? p.order_id),
      p,
      kind: p.kind,
      title: p.kind === 'remaining' ? 'Reste' : 'BL N°' + p.delivery_id,
      when: this.whenOf(p),
      modeLabel: p.mode != null && p.kind !== 'remaining' ? sales_order_mode.get(p.mode) ?? 'Mode ' + p.mode : '',
      stateLabel: getOrderLabel(p.order_state),
      stateColor: salesOrderColors[p.order_state as SalesOrderState] ?? '',
      canceledOrder: p.order_state === SalesOrderState.CANCELED,
      rental: p.doc_type === 'LOCATION',
      passage: p.passage ?? null,
      lines,
    };
  }

  private whenOf(p: TrackingPiece): string {
    if (p.kind === 'delivered') return p.realdatdel ? 'livré le ' + dayjs(p.realdatdel).format('DD/MM/YY') : 'livré';
    if (p.kind === 'remaining') return '';
    const slot = this.slots.find((s) => s.id === p.askslot)?.name;
    const day = p.askdatdel ? 'prévu le ' + frDay(p.askdatdel) + (slot ? ' ' + slot : '') : 'sans date';
    return day + (p.id_del_run ? ' · tournée ' + p.id_del_run : '');
  }

  private matches(v: PieceView): boolean {
    const f = this.filter;
    if (this.orderId) return true;
    if (f.store !== '-1' && String(v.p.store) !== f.store) return false;
    if (f.user !== '-1' && String(v.p.id_user) !== f.user) return false;
    return true;
  }

  // Une pièce qui sort de la vue sort aussi des sélections : on n'agit jamais sur ce qu'on ne voit pas.
  private applyLocal(): void {
    this.visible = this.pieces.filter((v) => this.matches(v));
    const groups = new Map<string, GroupView>();
    for (const v of this.visible) {
      const key = 'c' + v.p.customer_id;
      const g = groups.get(key);
      if (g) g.pieces.push(v); else groups.set(key, { key, name: v.p.customer_name || 'Client n° ' + v.p.customer_id, city: v.p.customer_city ?? '', pieces: [v] });
    }
    this.groups = [...groups.values()];
    const planned = new Set(this.visible.filter((v) => v.kind === 'planned').map((v) => v.p.delivery_id!));
    for (const id of [...this.selectedBl]) if (!planned.has(id)) this.selectedBl.delete(id);
    const remaining = new Map<number, LineView>();
    for (const v of this.visible) if (v.kind === 'remaining') for (const lv of v.lines) remaining.set(lv.l.id_order_l, lv);
    for (const [id, e] of [...this.cancel]) {
      const lv = remaining.get(id);
      if (!lv) this.cancel.delete(id); else e.max = +lv.l.qty;
    }
  }

  get hiddenCount(): number { return this.pieces.length - this.visible.length; }

  countKind(k: TrackingKind): number { return this.visible.filter((v) => v.kind === k).length; }

  isKind(k: TrackingKind): boolean { return this.filter.kinds.includes(k); }

  kindTooltip(k: TrackingKind): string {
    if (k === 'delivered') return 'BL réalisés : signés ou retournés de tournée. Allumés sans date, la période passe aux 30 derniers jours.';
    if (k === 'planned') return 'BL créés, pas encore livrés : tournée, emportée, retrait, dès que possible.';
    return "Ce qu'aucun BL ne porte encore : commandé moins tous les BL, annulations comprises.";
  }

  // Les BL livrés s'accumulent sans fin : allumés sans date, la période passe aux 30 derniers jours, visible dans les champs.
  toggleKind(k: TrackingKind): void {
    if (this.orderId) this.leaveOrderId();
    const on = !this.isKind(k);
    this.filter.kinds = TRACKING_KINDS.filter((x) => x === k ? on : this.isKind(x));
    if (on && k === 'delivered' && !this.filter.from) [this.filter.from, this.filter.to] = this.periodRange('days30');
    this.onServerFilterChange();
  }

  toggleCanceled(): void {
    if (this.orderId) this.leaveOrderId();
    this.filter.canceled = !this.filter.canceled;
    this.onServerFilterChange();
  }

  // Quatrième bouton : les BL des dossiers de location rejoignent Livrés et Programmés, jamais le reste (un matériel loué se pose et se reprend).
  toggleRental(): void {
    if (this.orderId) this.leaveOrderId();
    this.filter.rental = !this.filter.rental;
    this.onServerFilterChange();
  }

  countRental(): number { return this.visible.filter((v) => v.rental).length; }

  private onServerFilterChange(): void {
    this.load();
    this.syncUrl();
  }

  onSelectChange(): void {
    this.applyLocal();
    this.syncUrl();
  }

  customerChange(customer: any): void {
    if (this.orderId) this.leaveOrderId();
    if (customer && customer.id && customer.name) {
      this.filter.customer = customer.id.toString();
      this.customer = customer;
    } else {
      this.customer = null;
      delete this.filter.customer;
    }
    this.onServerFilterChange();
  }

  dateChange(date: any, type: 'from' | 'to'): void {
    if (this.orderId) this.leaveOrderId();
    this.filter[type] = date ?? undefined;
    this.onServerFilterChange();
  }

  // --- Produit : douchette ou saisie (GET /search/scan), ou choix dans la liste ; tous les conditionnements du produit comptent ---

  onProductText(text: string): void {
    const { raw } = splitQtyPrefix(String(text ?? '').trim());
    if (!raw) return;
    this.productsService.search_scan(raw).pipe(first()).subscribe({
      next: (res: ScanResult) => {
        const product = res.ppv?.lnk_product;
        if (res.ppv?.product && product) { this.setProduct(String(res.ppv.product), product.name ?? ''); return; }
        const rejection = scanRejection(res.scan, raw);
        if (rejection) this.alertService.warn(rejection);
        else if (!looksLikeFreeText(res.scan, raw)) this.alertService.warn('Code barre non trouvé : ' + scanLabel(res.scan, raw));
      },
      error: (e) => this.alertService.error('ERREUR Serveur : ' + (e?.error?.message ?? e?.error ?? e?.message ?? '')),
    });
  }

  onProductSelected(data: any): void {
    if (data === undefined) { if (this.filter.product) this.setProduct(null, ''); return; }
    const id = data?.ppv?.product ?? data?.id;
    if (!id) return;
    this.setProduct(String(id), data?.ppv?.lnk_product?.name ?? data?.name ?? '');
  }

  setProduct(id: string | null, name: string): void {
    if (!this.orderId && (id ?? undefined) === this.filter.product) return;   // même produit relu au blur : rien à relire
    if (this.orderId) this.leaveOrderId();
    if (id) { this.filter.product = id; this.productName = name || 'Produit n° ' + id; }
    else { delete this.filter.product; this.productName = ''; }
    this.onServerFilterChange();
  }

  // Le cumul du produit cherché, en unités de base, par sens (audit 31/10 #55) : une reprise de location (BL −1) se compte à part, jamais soustraite d'une pose.
  get productSummary(): ProductSummary | null {
    const product = this.filter.product && !this.orderId ? +this.filter.product : null;
    return product === null ? null : productSummaryOf(this.visible, product);
  }

  // --- Période (date de validation de la commande) ---

  public readonly periods: { key: Period; label: string; title: string }[] = [
    { key: 'day', label: 'Jour', title: "Commandes validées aujourd'hui" },
    { key: 'month', label: 'Mois', title: "Mois en cours : du 1er à aujourd'hui" },
    { key: 'days30', label: '30 j', title: "30 derniers jours, aujourd'hui compris" },
    { key: 'months12', label: '12 m', title: "12 derniers mois : du lendemain de la même date l'an dernier à aujourd'hui" },
  ];

  private periodRange(key: Period): [Date, Date] {
    const today = new Date();
    const y = today.getFullYear(), m = today.getMonth(), d = today.getDate();
    if (key === 'month') return [new Date(y, m, 1), today];
    if (key === 'days30') return [new Date(y, m, d - 29), today];
    if (key === 'months12') { const s = yearsBefore(today, 1); s.setDate(s.getDate() + 1); return [s, today]; }
    return [new Date(y, m, d), today];
  }

  isPeriod(key: Period): boolean {
    const [s, e] = this.periodRange(key);
    return pickedDateStr(this.filter.from) === pickedDateStr(s) && pickedDateStr(this.filter.to) === pickedDateStr(e);
  }

  setPeriod(key: Period): void {
    if (this.orderId) this.leaveOrderId();
    [this.filter.from, this.filter.to] = this.periodRange(key);
    this.onServerFilterChange();
  }

  // Effacer : la vue d'arrivée (programmés et restes, toutes dates, tous clients et produits).
  onClear(): void {
    this.filter = this.defaultFilter();
    this.customer = null;
    this.productName = '';
    this.orderId = null;
    this.showOrderId(null);
    this.onServerFilterChange();
  }

  clearLocal(): void {
    this.filter.store = '-1';
    this.filter.user = '-1';
    this.onSelectChange();
  }

  // --- « Id commande » : toutes les pièces d'une commande, livrées, programmées et reste, annulée comprise ---

  onOrderIdChange(value: string): void {
    const id = urlId(String(value ?? '').trim());
    this.orderIdHref = id ? this.router.serializeUrl(this.router.createUrlTree(['/sales/quotation', +id])) : null;
  }

  onOrderIdBlur(value: string): void {
    if (!this.alive || this.router.currentNavigation()) return;
    const id = urlId(String(value ?? '').trim());
    if (id === this.orderId) return;
    if (id) this.enterOrderId(id); else if (this.orderId) { this.leaveOrderId(); this.onServerFilterChange(); }
  }

  onOrderIdValidate(value: string): void {
    const id = urlId(String(value ?? '').trim());
    if (id && id !== this.orderId) this.enterOrderId(id);
  }

  private enterOrderId(id: string): void {
    this.orderId = id;
    this.showOrderId(id);
    this.rebuild();
    this.onServerFilterChange();
  }

  private leaveOrderId(): void {
    this.orderId = null;
    this.showOrderId(null);
  }

  private showOrderId(id: string | null): void {
    this.orderIdText = id ?? '';
    this.onOrderIdChange(this.orderIdText);
  }

  // --- URL = source des filtres, comme F4 et la V2 : un lien copié ou un Retour rend la même vue ---

  private applyUrl(q: ParamMap): void {
    if (this.ownWrites) return;
    this.readUrl(q);
    this.normalize();
    this.rebuild();
    this.load();
    this.syncUrl();
  }

  private readUrl(q: ParamMap): void {
    const o = this.defaultFilter();
    const id = urlId(q.get('id'));
    this.orderId = id;
    this.showOrderId(id);
    const kinds = q.has('kinds') ? (q.get('kinds') ?? '').split(',').filter((k): k is TrackingKind => (TRACKING_KINDS as string[]).includes(k)) : o.kinds;
    this.filter = {
      kinds: TRACKING_KINDS.filter((k) => kinds.includes(k)),
      from: urlDay(q.get('from')) ?? undefined,
      to: urlDay(q.get('to')) ?? undefined,
      canceled: q.get('canceled') === '1',
      rental: q.get('rental') === '1',
      store: urlId(q.get('store')) ?? o.store,
      user: urlId(q.get('user')) ?? o.user,
    };
    const customer = urlId(q.get('customer'));
    if (customer) this.filter.customer = customer;
    this.showCustomer(customer);
    const product = urlId(q.get('product'));
    if (product) this.filter.product = product;
    this.showProduct(product);
  }

  private showCustomer(id: string | null): void {
    if (id === null) { this.customer = null; return; }
    if (this.customer && String(this.customer.id) === id) return;
    this.customer = null;
    this.partnerService.getPartnerByid(+id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (partners: Partner[]) => {
        if (this.filter.customer !== id) return;
        if (partners?.[0]?.id) { this.customer = partners[0]; return; }
        delete this.filter.customer;
        this.onServerFilterChange();
      },
      error: (e) => {
        console.error('[DeliveryTrackingComponent] client du filtre illisible', e);
        if (this.filter.customer === id) this.customer = { id: +id, name: 'Client n° ' + id } as Partner;
      },
    });
  }

  // Produit lu dans l'URL : son nom vient des lignes chargées (rebuild), sans requête de plus.
  private showProduct(id: string | null): void {
    if (id === null) { this.productName = ''; return; }
    if (this.productName && this.filter.product === id) return;
    this.productName = 'Produit n° ' + id;
  }

  private normalize(): void {
    const known = (list: { id?: number }[], v?: string) => !v || v === '-1' || list.some((x) => String(x.id) === v);
    if (this.storesLoaded && !known(this.stores, this.filter.store)) this.filter.store = '-1';
    if (this.usersLoaded && !known(this.users, this.filter.user)) this.filter.user = '-1';
  }

  private reconcile(): void {
    const before = JSON.stringify(this.queryOf());
    this.normalize();
    if (JSON.stringify(this.queryOf()) !== before) { this.applyLocal(); this.syncUrl(); }
  }

  private queryOf(): Record<string, string> {
    if (this.orderId) return { id: this.orderId };
    const f = this.filter, q: Record<string, string> = {};
    q['kinds'] = f.kinds.join(',');
    q['from'] = pickedDateStr(f.from) ?? '';
    q['to'] = pickedDateStr(f.to) ?? '';
    if (f.customer) q['customer'] = f.customer;
    if (f.product) q['product'] = f.product;
    if (f.canceled) q['canceled'] = '1';
    if (f.rental) q['rental'] = '1';
    if (f.store !== '-1') q['store'] = f.store;
    if (f.user !== '-1') q['user'] = f.user;
    return q;
  }

  private syncUrl(): void {
    if (!this.alive || !this.started || this.syncPending) return;
    const nav = this.router.currentNavigation();
    if (nav && pathOf(nav.extractedUrl) !== pathOf(this.router.createUrlTree([]))) {
      this.syncPending = true;
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError), take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => setTimeout(() => { this.syncPending = false; this.syncUrl(); }));
      return;
    }
    const queryParams = this.queryOf();
    const cur = this.route.snapshot.queryParamMap;
    const keys = Object.keys(queryParams);
    if (cur.keys.length === keys.length && keys.every((k) => cur.get(k) === queryParams[k])) return;
    this.ownWrites++;
    this.router.navigate([], { queryParams, replaceUrl: true })
      .catch((e) => console.error('[DeliveryTrackingComponent] URL des filtres non écrite', e))
      .finally(() => this.ownWrites--);
  }

  // --- Affichage ---

  dayFr(day: string | null): string { return frDay(day); }

  qtyFr(v: number): string { return fmtQty(v); }

  userShortName(id: number | null): string {
    const u = this.users.find((x) => x.id === id && id !== -1);
    return u ? ((u.firstname ?? '') + ' ' + (u.lastname?.[0] ?? '')).trim() : '';
  }

  storeName(id: number | null): string {
    return id == null ? '' : this.stores.find((s) => s.id === id)?.name ?? 'Ets. ' + id;
  }

  // Ouvre la pièce dans la fenêtre de livraison : un BL par son rang ; un reste sans rang, pour planifier (la fenêtre crée le BL du reste).
  deliveryParams(v: PieceView): Record<string, number> | null {
    return v.p.bl_index != null ? { index: v.p.bl_index } : null;
  }

  // Passage de location : la fenêtre de livraison location, sur le BL du dossier, montre tout le passage (matériel et ventes).
  openPassage(v: PieceView): void {
    if (!v.passage || !v.p.passage_order) return;
    const ref = this.dialog.open(RentalDeliveryDialogComponent, { width: '90%', height: '90%', maxWidth: '95vw', data: { id: v.p.passage_order, deliveryId: v.passage } });
    ref.beforeClosed().pipe(first()).subscribe(() => { if (ref.componentInstance?.wrote) this.load(); });
  }

  // --- Passer à livré : les BL programmés cochés, event 703 comme au retour de tournée ---

  // Un BL d'un passage de location se coche avec les autres BL visibles du passage : le serveur les livre ensemble de toute façon.
  toggleBl(v: PieceView, checked?: boolean): void {
    if (v.kind !== 'planned' || v.p.delivery_id == null) return;
    const on = checked ?? !this.selectedBl.has(v.p.delivery_id);
    const same = v.passage ? this.visible.filter((x) => x.kind === 'planned' && x.passage === v.passage) : [v];
    for (const x of same) if (on) this.selectedBl.add(x.p.delivery_id!); else this.selectedBl.delete(x.p.delivery_id!);
  }

  markDelivered(): void {
    const chosen = this.visible.filter((v) => v.kind === 'planned' && this.selectedBl.has(v.p.delivery_id!));
    if (!chosen.length) { this.alertService.warn('Aucun BL programmé coché.'); return; }
    const ids = chosen.map((v) => v.p.delivery_id!);
    const withoutProof = chosen.filter((v) => !v.p.has_proof).length;
    const passages = new Set(chosen.filter((v) => v.passage).map((v) => v.passage)).size;
    askDate(this.dialog, {
      title: 'Déclarer livrés ?',
      html: `<b>${ids.length}</b> BL seront déclarés livrés le jour choisi. Un BL livré ne se modifie plus.`
        + (passages ? `<br><br>Location : chaque passage (${passages}) est livré en entier, BL du dossier et BL des ventes ensemble, même ceux qui ne sont pas affichés.` : '')
        + (withoutProof ? `<br><br>⚠ <b>${withoutProof}</b> d'entre eux n'ont ni scan du BL papier ni BL signé à l'écran.` : '')
        + '<br><small>Un jour à venir est daté de maintenant.</small>',
      warn: withoutProof > 0,
      label: 'Jour de livraison',
      day: dayjs(todayStr()).toDate(),
      confirm: 'Déclarer livrés',
    }).then((day) => {
      if (!day) return;
      this.tourReturnService.postDeliveryReturn$(ids, day).pipe(first(), this.spinner.block('Passage des BL à « livré »…')).subscribe({
        next: (response: any) => {
          if (response?.success === false) { this.alertService.error(response.msg || 'Passage à « livré » refusé'); return; }
          const done: number[] = response?.data?.done ?? [];
          const skipped: number[] = response?.data?.skipped ?? [];
          this.alertService.success(done.length + ' BL déclaré(s) livré(s)' + (skipped.length ? ' — ' + skipped.length + ' non passé(s) (déjà livré ou commande annulée) : ' + skipped.join(', ') : ''));
          this.selectedBl.clear();
          this.load();
        },
        error: (e) => {
          console.error(e);
          this.alertService.error('ERREUR Serveur : ' + (e?.error?.message ?? e?.error ?? e?.message ?? ''));
        },
      });
    });
  }

  // --- Annuler le reste : lignes de reste cochées, quantité saisissable, un bon d'annulation (event 705) par commande ---

  isCancelChecked(lv: LineView): boolean { return this.cancel.has(lv.l.id_order_l); }

  toggleCancel(v: PieceView, lv: LineView, checked: boolean): void {
    if (v.kind !== 'remaining') return;
    if (checked) this.cancel.set(lv.l.id_order_l, { orderId: v.p.order_id, customer: v.p.customer_name, name: lv.name, qty: +lv.l.qty, max: +lv.l.qty });
    else this.cancel.delete(lv.l.id_order_l);
  }

  pieceCancelState(v: PieceView): 'all' | 'some' | 'none' {
    const n = v.lines.filter((lv) => this.cancel.has(lv.l.id_order_l)).length;
    return n === 0 ? 'none' : n === v.lines.length ? 'all' : 'some';
  }

  togglePieceCancel(v: PieceView, checked: boolean): void {
    for (const lv of v.lines) this.toggleCancel(v, lv, checked);
  }

  // Produit arrêté chez le fabricant : toutes ses lignes de reste visibles, d'un clic.
  checkProductRemainders(): void {
    const product = this.filter.product ? +this.filter.product : null;
    if (product === null) return;
    for (const v of this.visible) if (v.kind === 'remaining') for (const lv of v.lines) if (lv.l.product === product) this.toggleCancel(v, lv, true);
  }

  cancelQty(lv: LineView): number | null { return this.cancel.get(lv.l.id_order_l)?.qty ?? null; }

  setCancelQty(lv: LineView, value: any): void {
    const e = this.cancel.get(lv.l.id_order_l);
    if (e) e.qty = value === '' || value === null || value === undefined ? null : +value;
  }

  isCancelBad(lv: LineView): boolean {
    const e = this.cancel.get(lv.l.id_order_l);
    return !!e && (!isQty(e.qty) || e.qty > e.max);
  }

  get cancelCount(): number { return this.cancel.size; }

  clearCancel(): void { this.cancel.clear(); }

  cancelRemaining(): void {
    const entries = [...this.cancel.entries()];
    if (!entries.length) { this.alertService.warn('Aucune ligne de reste cochée.'); return; }
    if (entries.some(([, e]) => !isQty(e.qty) || e.qty > e.max)) {
      this.alertService.error('Quantité à annuler en erreur : positive, au centième, et au plus le reste de la ligne.');
      return;
    }
    const byOrder = new Map<number, { orderId: number; customer: string; lines: DeliveryLineQty[]; total: number }>();
    for (const [id, e] of entries) {
      const g = byOrder.get(e.orderId) ?? { orderId: e.orderId, customer: e.customer, lines: [], total: 0 };
      g.lines.push({ id_order_l: id, qty: e.qty! });
      g.total = roundHalfAway(g.total + e.qty!, 2);
      byOrder.set(e.orderId, g);
    }
    const orders = [...byOrder.values()];
    const shown = orders.slice(0, 10).map((g) => `<li>Commande <b>${g.orderId}</b> · ${esc(g.customer)} : ${g.lines.length} ligne(s), ${fmtQty(g.total)} article(s)</li>`).join('');
    Swal.fire({
      title: 'Annuler le reste à livrer ?',
      html: `<b>${entries.length}</b> ligne(s) sur <b>${orders.length}</b> commande(s) ne seront jamais livrées :`
        + `<ul style="text-align: left; max-height: 220px; overflow-y: auto;">${shown}${orders.length > 10 ? `<li>… et ${orders.length - 10} autre(s) commande(s)</li>` : ''}</ul>`
        + 'Un bon d\'annulation par commande, définitif, qui ne se facture pas.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: orders.length > 1 ? `Générer les ${orders.length} bons d'annulation` : 'Générer le bon d\'annulation',
      confirmButtonColor: '#dc2626',
      cancelButtonText: 'Retour',
    }).then((res) => { if (res.isConfirmed) this.postCancels(orders); });
  }

  // Une commande après l'autre : un refus du serveur n'arrête pas les suivantes, il est listé à la fin avec son motif.
  private postCancels(orders: { orderId: number; customer: string; lines: DeliveryLineQty[] }[]): void {
    const one = (g: { orderId: number; customer: string; lines: DeliveryLineQty[] }): Observable<{ orderId: number; customer: string; ok: boolean; msg: string }> =>
      this.tourReturnService.postCancelRemaining$(g.orderId, g.lines).pipe(
        map((r: any) => ({ orderId: g.orderId, customer: g.customer, ok: r?.success !== false, msg: r?.msg ?? '' })),
        catchError((e) => of({ orderId: g.orderId, customer: g.customer, ok: false, msg: String(e?.error?.msg ?? e?.error?.message ?? e?.error ?? e?.message ?? 'Erreur serveur') })),
      );
    from(orders).pipe(concatMap(one), toArray(), this.spinner.block('Création des bons d\'annulation…')).subscribe((results) => {
      const failed = results.filter((r) => !r.ok);
      const done = results.length - failed.length;
      if (!failed.length) this.alertService.success(done + ' bon(s) d\'annulation créé(s) : le reste de ' + done + ' commande(s) est soldé.');
      else Swal.fire({
        title: 'Annulation partielle',
        html: `${done} bon(s) d'annulation créé(s), ${failed.length} refusé(s) :<ul style="text-align: left; max-height: 220px; overflow-y: auto;">`
          + failed.map((r) => `<li>Commande <b>${r.orderId}</b> · ${esc(r.customer)} : ${esc(r.msg)}</li>`).join('') + '</ul>',
        icon: 'warning',
        confirmButtonText: 'Fermer',
      });
      this.cancel.clear();
      this.load();
    });
  }
}
